import React, { useEffect, useState } from "react";
import BreadCrumb from "../components/BreadCrumb";
import Meta from "../components/Meta";
import ReactStars from "react-rating-stars-component";
import ProductCard from "../components/ProductCard";
import Color from "../components/Color";
import Container from "../components/Container";
import { useDispatch, useSelector } from "react-redux";
import { getAllProducts } from "../features/products/productSlilce";
import { Link } from "react-router-dom";
import { 
  BsSearch, 
  BsFilter, 
  BsX, 
  BsGrid3X3, 
  BsGrid, 
  BsList,
  BsStarFill,
  BsStar,
  BsStarHalf,
  BsChevronLeft,
  BsChevronRight
} from "react-icons/bs";
import { useTranslation } from "react-i18next";

const OurStore = () => {
  // Use the hook at the top level
  const { t, i18n } = useTranslation();
  
  const [grid, setGrid] = useState(4);
  const productState = useSelector((state) => state?.product?.product);
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [translatedProducts, setTranslatedProducts] = useState([]);
  const [forceUpdate, setForceUpdate] = useState(0);

  // Get current language from i18n
  const currentLanguage = i18n.language;

  //filter state
  const [tag, setTag] = useState(null);
  const [category, setCategory] = useState(null);
  const [brand, setBrand] = useState(null);
  const [minPrice, setminPrice] = useState("");
  const [maxPrice, setmaxPrice] = useState("");
  const [sort, setSort] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [ratingFilter, setRatingFilter] = useState(null);

  // Clear all filters
  const clearAllFilters = () => {
    setTag(null);
    setCategory(null);
    setBrand(null);
    setminPrice("");
    setmaxPrice("");
    setSort("");
    setSearchQuery("");
    setRatingFilter(null);
  };

  useEffect(() => {
    let newBrands = [];
    let category = [];
    let newtags = [];

    // Ensure productState is an array before processing
    if (Array.isArray(productState)) {
      for (let index = 0; index < productState?.length; index++) {
        const element = productState[index];
        if (element.brand) newBrands.push(element.brand);
        if (element.category) category.push(element.category);
        if (element.tags) newtags.push(element.tags);
      }
      setBrands([...new Set(newBrands)]);
      setCategories([...new Set(category)]);
      setTags([...new Set(newtags)]);
    }
  }, [productState]);

  const dispatch = useDispatch();
  
  useEffect(() => {
    getProducts();
  }, [sort, tag, brand, category, minPrice, maxPrice, ratingFilter]);

  const getProducts = () => {
    setLoading(true);
    const filterData = {
      sort,
      tag,
      brand,
      category,
      minPrice: minPrice || null,
      maxPrice: maxPrice || null,
      rating: ratingFilter
    };
    
    dispatch(getAllProducts(filterData)).finally(() => {
      setLoading(false);
    });
  };

  // Filter products by search query
  // Ensure productState is an array before filtering
  const filteredProducts = Array.isArray(translatedProducts) ? translatedProducts?.filter(product => 
    product.translatedTitle?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    product.translatedBrand?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    product.translatedCategory?.toLowerCase().includes(searchQuery.toLowerCase())
  ) : [];

  // Update translated products when productState or currentLanguage changes
  useEffect(() => {
    if (Array.isArray(productState)) {
      const translated = productState.map(product => ({
        ...product,
        translatedTitle: product?.translations?.[currentLanguage]?.title || product?.title || "",
        translatedBrand: product?.translations?.[currentLanguage]?.brand || product?.brand || "",
        translatedCategory: product?.translations?.[currentLanguage]?.category || product?.category || ""
      }));
      setTranslatedProducts(translated);
    }
  }, [productState, currentLanguage, forceUpdate]);

  // Listen for language change events
  useEffect(() => {
    const handleLanguageChange = () => {
      setForceUpdate(prev => prev + 1);
    };

    window.addEventListener('languageChanged', handleLanguageChange);
    return () => {
      window.removeEventListener('languageChanged', handleLanguageChange);
    };
  }, []);

  // Get unique values for filters with translated content
  const uniqueBrands = [...new Set(
    Array.isArray(translatedProducts) ? translatedProducts.map(item => 
      item?.translatedBrand || ""
    ).filter(Boolean) : []
  )];
  
  const uniqueCategories = [...new Set(
    Array.isArray(translatedProducts) ? translatedProducts.map(item => 
      item?.translatedCategory || ""
    ).filter(Boolean) : []
  )];
  
  const uniqueTags = [...new Set(
    Array.isArray(translatedProducts) ? translatedProducts.flatMap(item => 
      item?.tags || []
    ).filter(Boolean) : []
  )];

  // Schema markup for structured data
  const schemaMarkup = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "VEDA Mythological Toys Store",
    "description": "Browse our collection of educational mythological toys, games, and puzzles for kids based on Indian mythology.",
    "url": "https://vedamerchandise.com/products",
    "publisher": {
      "@type": "Organization",
      "name": "VEDA Merchandise",
      "logo": {
        "@type": "ImageObject",
        "url": "https://vedamerchandise.com/logo.svg"
      }
    }
  };

  return (
    <>
      <Meta 
        title="VEDA Mythological Toys Store - Educational Toys for Kids"
        description="Browse our collection of educational mythological toys, games, and puzzles for kids based on Indian mythology."
        keywords="mythological toys, Indian mythology toys, educational toys, kids toys, VEDA toys, Ramayana toys, Mahabharata toys"
        author="VEDA Merchandise"
        ogTitle="VEDA Mythological Toys Store - Educational Toys for Kids"
        ogDescription="Browse our collection of educational mythological toys, games, and puzzles for kids based on Indian mythology."
        ogImage="https://vedamerchandise.com/store-preview.jpg"
        ogUrl="https://vedamerchandise.com/products"
        ogType="website"
        twitterCard="summary_large_image"
        twitterTitle="VEDA Mythological Toys Store - Educational Toys for Kids"
        twitterDescription="Browse our collection of educational mythological toys, games, and puzzles for kids based on Indian mythology."
        twitterImage="https://vedamerchandise.com/store-preview.jpg"
        robots="index, follow"
        canonicalUrl="https://vedamerchandise.com/products"
        schemaMarkup={schemaMarkup}
      />
      <Container class1="store-wrapper home-wrapper-2 py-5">
        <div className="row">
          {/* Mobile Filter Toggle */}
          <div className="col-12 d-lg-none mb-3">
            <button 
              className="btn btn-outline-primary w-100 d-flex align-items-center justify-content-center gap-2"
              onClick={() => setShowFilters(!showFilters)}
            >
              <BsFilter />
              {showFilters ? 'Hide Filters' : 'Show Filters'}
            </button>
          </div>

          {/* Filters Sidebar */}
          <div className={`col-lg-3 ${showFilters ? 'd-block' : 'd-none'} d-lg-block`}>
            <div className="filter-card mb-3 rounded-3xl shadow-lg p-4 bg-white">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <h3 className="filter-title mb-0 text-xl font-bold bg-yellow-200 py-2 px-6 rounded-full">Filters</h3>
                <button 
                  className="btn btn-sm btn-outline-secondary rounded-full px-3 py-1 text-sm"
                  onClick={clearAllFilters}
                >
                  Clear All
                </button>
              </div>
              
              {/* Search */}
              <div className="mb-3">
                <div className="relative">
                  <input
                    type="text"
                    className="form-control rounded-full pl-4 pr-10 py-2 border border-gray-300 focus:border-red-500 focus:ring-1 focus:ring-red-500"
                    placeholder="Search products..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500">
                    <BsSearch />
                  </div>
                </div>
              </div>

              {/* Categories */}
              <div className="mb-3">
                <h5 className="sub-title font-bold text-lg mb-2">Categories</h5>
                <div className="filter-options">
                  <div className="form-check mb-2">
                    <input
                      className="form-check-input me-2"
                      type="radio"
                      name="category"
                      id="category-all"
                      checked={category === null}
                      onChange={() => setCategory(null)}
                    />
                    <label className="form-check-label" htmlFor="category-all">
                      All Categories
                    </label>
                  </div>
                  {uniqueCategories.map((cat, index) => (
                    <div key={index} className="form-check mb-2">
                      <input
                        className="form-check-input me-2"
                        type="radio"
                        name="category"
                        id={`category-${index}`}
                        checked={category === cat}
                        onChange={() => setCategory(cat)}
                      />
                      <label className="form-check-label" htmlFor={`category-${index}`}>
                        {cat}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              {/* Brands */}
              <div className="mb-3">
                <h5 className="sub-title font-bold text-lg mb-2">Brands</h5>
                <div className="filter-options">
                  <div className="form-check mb-2">
                    <input
                      className="form-check-input me-2"
                      type="radio"
                      name="brand"
                      id="brand-all"
                      checked={brand === null}
                      onChange={() => setBrand(null)}
                    />
                    <label className="form-check-label" htmlFor="brand-all">
                      All Brands
                    </label>
                  </div>
                  {uniqueBrands.map((brandItem, index) => (
                    <div key={index} className="form-check mb-2">
                      <input
                        className="form-check-input me-2"
                        type="radio"
                        name="brand"
                        id={`brand-${index}`}
                        checked={brand === brandItem}
                        onChange={() => setBrand(brandItem)}
                      />
                      <label className="form-check-label" htmlFor={`brand-${index}`}>
                        {brandItem}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              {/* Price Range */}
              <div className="mb-3">
                <h5 className="sub-title font-bold text-lg mb-2">Price Range</h5>
                <div className="d-flex align-items-center gap-2">
                  <div className="form-floating flex-fill">
                    <input
                      type="number"
                      className="form-control rounded-full py-2 border border-gray-300 focus:border-red-500 focus:ring-1 focus:ring-red-500"
                      id="minPrice"
                      placeholder="Min"
                      value={minPrice}
                      onChange={(e) => setminPrice(e.target.value)}
                    />
                    <label htmlFor="minPrice">Min</label>
                  </div>
                  <div className="form-floating flex-fill">
                    <input
                      type="number"
                      className="form-control rounded-full py-2 border border-gray-300 focus:border-red-500 focus:ring-1 focus:ring-red-500"
                      id="maxPrice"
                      placeholder="Max"
                      value={maxPrice}
                      onChange={(e) => setmaxPrice(e.target.value)}
                    />
                    <label htmlFor="maxPrice">Max</label>
                  </div>
                </div>
              </div>

              {/* Rating Filter */}
              <div className="mb-3">
                <h5 className="sub-title font-bold text-lg mb-2">Rating</h5>
                <div className="filter-options">
                  <div className="form-check mb-2">
                    <input
                      className="form-check-input me-2"
                      type="radio"
                      name="rating"
                      id="rating-all"
                      checked={ratingFilter === null}
                      onChange={() => setRatingFilter(null)}
                    />
                    <label className="form-check-label" htmlFor="rating-all">
                      All Ratings
                    </label>
                  </div>
                  {[4, 3, 2, 1].map((rating) => (
                    <div key={rating} className="form-check mb-2">
                      <input
                        className="form-check-input me-2"
                        type="radio"
                        name="rating"
                        id={`rating-${rating}`}
                        checked={ratingFilter === rating}
                        onChange={() => setRatingFilter(rating)}
                      />
                      <label className="form-check-label d-flex align-items-center gap-2" htmlFor={`rating-${rating}`}>
                        {rating}+ Stars
                        <div className="d-flex">
                          {[...Array(5)].map((_, i) => (
                            <span key={i}>
                              {i < rating ? <BsStarFill className="text-yellow-400" size={12} /> : <BsStar className="text-gray-300" size={12} />}
                            </span>
                          ))}
                        </div>
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              {/* Tags */}
              <div className="mb-3">
                <h5 className="sub-title font-bold text-lg mb-2">Tags</h5>
                <div className="product-tags d-flex flex-wrap gap-2">
                  {uniqueTags.map((tagItem, index) => (
                    <span
                      key={index}
                      onClick={() => setTag(tagItem)}
                      className={`badge cursor-pointer rounded-full py-2 px-3 text-sm ${
                        tag === tagItem 
                          ? 'bg-red-500 text-white' 
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {tagItem}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Products Section */}
          <div className="col-lg-9">
            {/* Sort and Grid Controls */}
            <div className="filter-sort-grid mb-4 p-4 bg-white rounded-3xl shadow-lg">
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                <div className="d-flex align-items-center gap-3">
                  <p className="mb-0 fw-semibold">Sort By:</p>
                  <select
                    className="form-select rounded-full py-2 border border-gray-300 focus:border-red-500 focus:ring-1 focus:ring-red-500"
                    value={sort}
                    onChange={(e) => setSort(e.target.value)}
                  >
                    <option value="">Default</option>
                    <option value="title">Alphabetically, A-Z</option>
                    <option value="-title">Alphabetically, Z-A</option>
                    <option value="price">Price, low to high</option>
                    <option value="-price">Price, high to low</option>
                    <option value="createdAt">Date, old to new</option>
                    <option value="-createdAt">Date, new to old</option>
                    <option value="totalrating">Rating, high to low</option>
                    <option value="-totalrating">Rating, low to high</option>
                  </select>
                </div>
                
                <div className="d-flex align-items-center gap-3">
                  <p className="totalproducts mb-0 fw-semibold">
                    {filteredProducts?.length || 0} Products
                  </p>
                  
                  {/* Grid View Controls */}
                  <div className="d-flex gap-2 align-items-center">
                    <button
                      onClick={() => setGrid(3)}
                      className={`btn btn-sm rounded-full p-2 ${
                        grid === 3 
                          ? 'bg-red-500 text-white border-red-500' 
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'
                      }`}
                      title="3 Columns"
                    >
                      <BsGrid3X3 />
                    </button>
                    <button
                      onClick={() => setGrid(4)}
                      className={`btn btn-sm rounded-full p-2 ${
                        grid === 4 
                          ? 'bg-red-500 text-white border-red-500' 
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'
                      }`}
                      title="4 Columns"
                    >
                      <BsGrid />
                    </button>
                    <button
                      onClick={() => setGrid(6)}
                      className={`btn btn-sm rounded-full p-2 ${
                        grid === 6 
                          ? 'bg-red-500 text-white border-red-500' 
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'
                      }`}
                      title="6 Columns"
                    >
                      <BsList />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Active Filters Display */}
            {(tag || brand || category || minPrice || maxPrice || sort || ratingFilter) && (
              <div className="active-filters mb-3 p-4 bg-white rounded-3xl shadow-lg">
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <span className="fw-semibold">Active Filters:</span>
                  {tag && (
                    <span className="badge bg-red-500 text-white d-flex align-items-center gap-1 rounded-full px-3 py-1">
                      Tag: {tag}
                      <BsX 
                        className="cursor-pointer" 
                        onClick={() => setTag(null)}
                        size={16}
                      />
                    </span>
                  )}
                  {brand && (
                    <span className="badge bg-red-500 text-white d-flex align-items-center gap-1 rounded-full px-3 py-1">
                      Brand: {brand}
                      <BsX 
                        className="cursor-pointer" 
                        onClick={() => setBrand(null)}
                        size={16}
                      />
                    </span>
                  )}
                  {category && (
                    <span className="badge bg-red-500 text-white d-flex align-items-center gap-1 rounded-full px-3 py-1">
                      Category: {category}
                      <BsX 
                        className="cursor-pointer" 
                        onClick={() => setCategory(null)}
                        size={16}
                      />
                    </span>
                  )}
                  {(minPrice || maxPrice) && (
                    <span className="badge bg-red-500 text-white d-flex align-items-center gap-1 rounded-full px-3 py-1">
                      Price: {minPrice || '0'} - {maxPrice || '∞'}
                      <BsX 
                        className="cursor-pointer" 
                        onClick={() => {setminPrice(''); setmaxPrice('');}}
                        size={16}
                      />
                    </span>
                  )}
                  {ratingFilter && (
                    <span className="badge bg-red-500 text-white d-flex align-items-center gap-1 rounded-full px-3 py-1">
                      Rating: {ratingFilter}+
                      <BsX 
                        className="cursor-pointer" 
                        onClick={() => setRatingFilter(null)}
                        size={16}
                      />
                    </span>
                  )}
                  <button 
                    className="btn btn-sm btn-outline-secondary rounded-full px-3 py-1"
                    onClick={clearAllFilters}
                  >
                    Clear All
                  </button>
                </div>
              </div>
            )}

            {/* Products List */}
            <div className="products-list pb-5">
              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-red-500" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-2 text-red-500">Loading products...</p>
                </div>
              ) : Array.isArray(filteredProducts) && filteredProducts.length > 0 ? (
                <div className="row g-3">
                  <ProductCard
                    data={filteredProducts}
                    grid={grid}
                  />
                </div>
              ) : (
                <div className="text-center py-5 bg-white rounded-3xl shadow-lg p-8">
                  <div className="empty-state">
                    <h4 className="text-2xl font-bold mb-2">No products found</h4>
                    <p className="text-gray-600 mb-4">Try adjusting your filters or search terms</p>
                    <button 
                      className="inline-flex items-center bg-red-500 hover:bg-red-600 text-white px-6 py-2 rounded-full font-semibold transition-colors duration-300"
                      onClick={clearAllFilters}
                    >
                      Clear All Filters
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </Container>
    </>
  );
};

export default OurStore;